import { useState } from 'react';
import { MODULE_DEFINITIONS, FURNITURE_TYPES, getTemplatesByCategory, formatPrice } from '@/lib/furnitureData';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Plus, GripVertical, Box, Layers, RectangleHorizontal, ArrowDownFromLine, Minus,
  Wand2, ChevronDown, ChevronRight, LayoutTemplate,
} from 'lucide-react';
import ParametricDialog from './ParametricDialog';

const MODULE_ICONS = {
  body: Box, frame: Box, headboard: RectangleHorizontal,
  door: Layers, shelf: Minus, board: Minus,
  drawer: ArrowDownFromLine, leg: GripVertical, hanging_rod: Minus,
  back_panel: Layers, tabletop: RectangleHorizontal, top_panel: RectangleHorizontal,
  countertop: RectangleHorizontal, crossbar: Minus, bracket: GripVertical,
  slat: Minus,
};

const PREVIEW_COLORS = {
  body: '#D8C8A9',
  frame: '#D8C8A9',
  tabletop: '#C8B089',
  top_panel: '#C8B089',
  countertop: '#C8B089',
  headboard: '#C8B089',
  door: '#B08C5A',
  drawer: '#A67947',
  shelf: '#CDB48B',
  board: '#CDB48B',
  leg: '#3E3E3E',
  crossbar: '#5A5A5A',
  bracket: '#666666',
  hanging_rod: '#7A7A7A',
  back_panel: '#E6DCC8',
  slat: '#B99966',
};

function TemplatePreview({ template }) {
  const modules = template?.modules || [];
  if (modules.length === 0) {
    return <div className="w-full h-full bg-[#F0EDE8]" />;
  }

  const xMin = Math.min(...modules.map(m => m.x || 0));
  const yMin = Math.min(...modules.map(m => m.y || 0));
  const xMax = Math.max(...modules.map(m => (m.x || 0) + (m.width || 1)));
  const yMax = Math.max(...modules.map(m => (m.y || 0) + (m.height || 1)));

  const width = Math.max(1, xMax - xMin);
  const height = Math.max(1, yMax - yMin);
  const pad = 6;
  const viewW = width + pad * 2;
  const viewH = height + pad * 2;

  return (
    <svg className="w-full h-full" viewBox={`0 0 ${viewW} ${viewH}`} preserveAspectRatio="xMidYMid meet">
      <rect x="0" y="0" width={viewW} height={viewH} fill="#F5F2ED" />
      {modules.map((m, i) => (
        <rect
          key={`${template.id}-${m.id || i}`}
          x={(m.x || 0) - xMin + pad}
          y={(m.y || 0) - yMin + pad}
          width={Math.max(1, m.width || 1)}
          height={Math.max(1, m.height || 1)}
          fill={m.color_hex || PREVIEW_COLORS[m.type] || '#BBA07A'}
          fillOpacity={m.type === 'back_panel' ? 0.45 : 0.95}
          stroke="#FFFFFF"
          strokeWidth="0.75"
          rx="0.8"
        />
      ))}
    </svg>
  );
}

export default function ModuleLibrary({
  furnitureType, onChangeFurnitureType, onAddModule, onApplyTemplate, onGenerateParametric,
  templatesModalOpen: controlledTemplatesModalOpen,
  onTemplatesModalOpenChange,
  showTemplatesTrigger = true,
}) {
  const modules = MODULE_DEFINITIONS[furnitureType] || [];
  const templates = getTemplatesByCategory(furnitureType);
  const [parametricOpen, setParametricOpen] = useState(false);
  const [localTemplatesModalOpen, setLocalTemplatesModalOpen] = useState(false);
  const [partsOpen, setPartsOpen] = useState(true);
  const templatesModalOpen = controlledTemplatesModalOpen ?? localTemplatesModalOpen;
  const setTemplatesModalOpen = onTemplatesModalOpenChange ?? setLocalTemplatesModalOpen;

  const handleDragStart = (e, moduleDef) => {
    e.dataTransfer.setData('application/json', JSON.stringify(moduleDef));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="w-64 bg-[#FAFAFA] border-r border-[#E0DDD8] flex flex-col shrink-0" data-testid="module-library">
      <div className="p-4 border-b border-[#E0DDD8]">
        <label className="text-xs font-medium text-[#5C5C5C] mb-2 block">Тип мебели</label>
        <Select value={furnitureType} onValueChange={onChangeFurnitureType}>
          <SelectTrigger className="h-9 text-sm rounded-sm border-[#E0DDD8]" data-testid="furniture-type-select">
            <SelectValue placeholder="Выберите тип" />
          </SelectTrigger>
          <SelectContent>
            {FURNITURE_TYPES.map(ft => (
              <SelectItem key={ft.id} value={ft.id} data-testid={`furniture-type-${ft.id}`}>
                {ft.nameRu}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={() => setParametricOpen(true)}
          className="w-full mt-3 h-8 bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm text-xs font-semibold"
          data-testid="open-parametric-btn"
        >
          <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Параметрический мастер
        </Button>

      </div>

      {/* Templates modal trigger */}
      {showTemplatesTrigger && (
        <div className="p-3 border-b border-[#E0DDD8] bg-[#C25934]/[0.04]">
          <Button
            onClick={() => setTemplatesModalOpen(true)}
            variant="outline"
            className="w-full h-8 rounded-sm border-[#C25934]/40 bg-transparent text-[11px] font-bold text-[#C25934] uppercase tracking-wider hover:bg-[#C25934]/10"
            data-testid="open-templates-modal-btn"
          >
            <span className="flex items-center gap-1.5">
              <LayoutTemplate className="w-3.5 h-3.5" /> Готовые конструкции
              <span className="mono-text text-[9px] text-[#C25934]/80 bg-[#C25934]/10 rounded-sm px-1.5 py-0.5">
                {templates.length}
              </span>
            </span>
          </Button>
        </div>
      )}

      {/* Atomic parts */}
      <div className="p-3 flex-1 min-h-0 flex flex-col">
        <button
          onClick={() => setPartsOpen(o => !o)}
          className="w-full flex items-center justify-between text-[11px] font-semibold text-[#5C5C5C] uppercase tracking-wider hover:text-[#C25934]"
          data-testid="toggle-parts"
        >
          <span>Детали</span>
          {partsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        {partsOpen && (
          <div className="mt-2 space-y-1 overflow-y-auto custom-scrollbar pr-1 flex-1 min-h-0">
            {modules.map((mod, idx) => {
              const Icon = MODULE_ICONS[mod.type] || Box;
              return (
                <div
                  key={`${mod.type}-${idx}`}
                  className="library-module flex items-center gap-2.5 p-2 rounded-sm border border-transparent hover:border-[#E0DDD8] group"
                  draggable
                  onDragStart={(e) => handleDragStart(e, mod)}
                  data-testid={`module-${mod.type}`}
                >
                  <div className="w-6 h-6 rounded-sm bg-[#E8E5E1] flex items-center justify-center shrink-0">
                    <Icon className="w-3 h-3 text-[#5C5C5C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-[#1C1C1C] truncate">{mod.nameRu}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAddModule(mod); }}
                    className="shrink-0 w-5 h-5 flex items-center justify-center rounded-sm hover:bg-[#C25934]/10 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`add-module-${mod.type}`}
                    draggable={false}
                  >
                    <Plus className="w-3 h-3 text-[#C25934]" />
                  </button>
                </div>
              );
            })}
            {modules.length === 0 && (
              <p className="text-xs text-[#5C5C5C] text-center py-3">Выберите тип мебели</p>
            )}
          </div>
        )}
      </div>

      <ParametricDialog
        open={parametricOpen}
        onClose={() => setParametricOpen(false)}
        category={furnitureType}
        onGenerate={onGenerateParametric}
      />
      <Dialog open={templatesModalOpen} onOpenChange={setTemplatesModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Готовые конструкции</DialogTitle>
          </DialogHeader>
          <div className="mt-1 overflow-y-auto custom-scrollbar pr-1">
            {templates.length === 0 ? (
              <p className="text-[12px] text-[#5C5C5C] text-center py-6">Нет готовых конструкций для этого типа</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => {
                      onApplyTemplate(t);
                      setTemplatesModalOpen(false);
                    }}
                    className="w-full flex flex-col gap-2 p-2 rounded-sm border border-transparent hover:border-[#C25934]/40 hover:bg-[#C25934]/5 transition-colors group text-left"
                    data-testid={`tpl-modal-${t.id}`}
                    title={t.description_ru}
                  >
                    <div className="w-full aspect-[4/3] rounded-sm overflow-hidden bg-[#E8E5E1] shrink-0">
                      <TemplatePreview template={t} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-[#1C1C1C] truncate">{t.name_ru}</p>
                      <p className="mono-text text-[11px] text-[#5C5C5C]">
                        {t.modules.length} дет. · {formatPrice(t.base_price)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
